import type { IEventData } from './types'
import { createEventData } from './events'
import {
  getOrCreateSessionData,
  persistSessionData,
} from '@/utils/tracking/session'
import { generateDeviceFingerprint } from '@/utils/tracking/fingerprint'
import { sendTrackingEvent, sendAscEvent } from '@/utils/tracking/api'
import type { IAscEventPayload, IAscItem } from '@/utils/tracking/types'

// ASC event name mapping
const ASC_EVENT_MAP: Record<
  string,
  'view_item_list' | 'select_item' | 'view_item'
> = {
  placement_load: 'view_item_list',
  placement_view: 'view_item_list',
  vehicle_view: 'view_item_list', // list impression
  vehicle_click: 'select_item',
}

const setupEventListeners = (trackEvent: Function) => {
  window.addEventListener('beforeunload', () => {
    sessionStorage.setItem('lastUnload', new Date().toISOString())
  })

  /*  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      trackEvent('page_visible');
    }
  });*/

  // Stop auto tracking for now
  /*  const events = ['click', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  events.forEach(eventName => {
    document.addEventListener(eventName, () => {
      const sessionData = getOrCreateSessionData();
      persistSessionData(sessionData);
    });
  });*/
}

export const initializeTracking = (accountId: string, placementId: string) => {
  const createTrackingEvent = (
    accountId: string,
    entityId: string,
    eventType: string,
    placement: string,
    additionalData: Record<string, any> = {},
  ) => {
    // Create and persist session data synchronously
    const sessionData = persistSessionData(getOrCreateSessionData())
    const deviceFingerprint = generateDeviceFingerprint()
    const eventData = createEventData(
      accountId,
      sessionData,
      eventType,
      placement,
      additionalData,
      deviceFingerprint,
    )

    // console.log('Tracking event:', eventData)

    sendTrackingEvent(accountId, entityId, eventData)

    // Also emit ASC-standard event if mapping exists
    const ascName = ASC_EVENT_MAP[eventType]
    if (ascName) {
      const ascPayload: IAscEventPayload = buildAscPayload(
        ascName,
        placement,
        entityId,
        additionalData,
      )
      sendAscEvent(ascPayload)
    }

    return eventData
  }

  setupEventListeners(createTrackingEvent)

  // Track initial page load
  createTrackingEvent(accountId, placementId, 'placement_load', placementId)

  return {
    trackEvent: createTrackingEvent,
  }
}

export type { ISessionData, IEventData } from './types'

const buildAscPayload = (
  eventName: IAscEventPayload['event_name'],
  placementId: string,
  entityId: string,
  additionalData: Record<string, any> = {},
): IAscEventPayload => {
  const base: IAscEventPayload = {
    event_name: eventName,
  }

  // Placement-level fields
  if (eventName === 'view_item_list' || eventName === 'select_item') {
    base.item_list_id = placementId
    base.item_list_name = 'micro_srp'
  }

  // Build item when VIN/vehicle data is present
  const vin = additionalData?.vin || additionalData?.vehicle?.vin || entityId
  const make = additionalData?.make || additionalData?.vehicle?.make
  const model = additionalData?.model || additionalData?.vehicle?.model
  const year = additionalData?.year || additionalData?.vehicle?.year
  const trim = additionalData?.trim || additionalData?.vehicle?.trim
  const priceRaw =
    additionalData?.price ??
    additionalData?.displayPrice ??
    additionalData?.display_price ??
    additionalData?.vehicle?.price
  const index = additionalData?.index

  const hasItem = Boolean(vin || make || model)
  if (hasItem) {
    // Normalize price to a number when possible
    let priceValue: number | undefined
    if (typeof priceRaw === 'number') {
      priceValue = priceRaw
    } else if (typeof priceRaw === 'string') {
      const normalized = priceRaw.replace(/[^0-9.-]/g, '')
      const parsed = Number(normalized)
      if (!Number.isNaN(parsed)) {
        priceValue = parsed
      }
    }

    const item: IAscItem = {
      item_id: String(vin ?? entityId),
      item_name:
        [year, make, model, trim].filter(Boolean).join(' ').trim() || undefined,
      item_brand: make || undefined,
      item_category: model || undefined,
      item_variant: trim || undefined,
      price: priceValue,
      index: typeof index === 'number' ? index : undefined,
    }

    base.items = [item]
  }

  // Currency when price present
  if (base.items?.[0]?.price != null) {
    base.currency = 'USD'
  }

  return base
}
